// /app/frontend/src/pages/risk/dpia/steps/RisksStep.jsx
import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Separator } from '../../../../components/ui/separator';
import { Plus, Trash2, AlertTriangle, Library, Shield, X, Sparkles } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { getRiskLevel, getRiskBadgeColor, RISK_LEVELS, LIKELIHOOD_SCALE, IMPACT_SCALE } from '../constants';

export default function RisksStep({ 
  formData, 
  updateField, 
  openRiskLibrary, 
  openControlLibrary,
  removeSelectedControl,
  estimatingRisks = {}
}) {
  const addRisk = () => {
    const newRisk = {
      id: uuidv4(),
      risk_number: formData.risks.length + 1,
      description: '',
      impact_before: 3,
      likelihood_before: 3,
      score_before: 9,
      level_before: 'moderate',
      existing_controls: '',
      impact_after: 2,
      likelihood_after: 2,
      score_after: 4,
      level_after: 'low',
      additional_controls: '',
      measures_approved: false,
      control_owner: '',
      implementation_date: '',
    };
    updateField('risks', [...formData.risks, newRisk]);
  };

  const updateRisk = (riskId, field, value) => {
    const updated = formData.risks.map(risk => {
      if (risk.id !== riskId) return risk;
      
      const updatedRisk = { ...risk, [field]: value };
      
      // Recalculate scores when impact/likelihood changes
      if (field.includes('impact_') || field.includes('likelihood_')) {
        if (field.includes('before')) {
          updatedRisk.score_before = updatedRisk.impact_before * updatedRisk.likelihood_before;
          updatedRisk.level_before = getRiskLevel(updatedRisk.score_before);
        } else {
          updatedRisk.score_after = updatedRisk.impact_after * updatedRisk.likelihood_after;
          updatedRisk.level_after = getRiskLevel(updatedRisk.score_after);
        }
      }
      
      return updatedRisk;
    });
    updateField('risks', updated);
  };

  const removeRisk = (riskId) => {
    const updated = formData.risks
      .filter(r => r.id !== riskId)
      .map((r, idx) => ({ ...r, risk_number: idx + 1 }));
    updateField('risks', updated);
  };

  // Get label for impact/likelihood value
  const getLikelihoodLabel = (value) => LIKELIHOOD_SCALE.find(s => s.value === value)?.label || value;
  const getImpactLabel = (value) => IMPACT_SCALE.find(s => s.value === value)?.label || value;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Risk Assessment</h4>
          <p className="text-sm text-muted-foreground">Add risks and assess them before and after controls</p>
        </div>
        <div className="flex gap-2">
          {openRiskLibrary && (
            <Button variant="outline" size="sm" onClick={openRiskLibrary}>
              <Library className="h-4 w-4 mr-2" /> Select from Risk Register
            </Button>
          )}
          <Button variant="default" size="sm" onClick={addRisk}>
            <Plus className="h-4 w-4 mr-2" /> Add Custom Risk
          </Button>
        </div>
      </div>

      {formData.risks.map((risk) => (
        <Card key={risk.id} className="overflow-hidden">
          <CardHeader className="bg-muted/50 py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Risk #{risk.risk_number}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => removeRisk(risk.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="space-y-2">
              <Label>Risk Description</Label>
              <Textarea 
                value={risk.description}
                onChange={(e) => updateRisk(risk.id, 'description', e.target.value)}
                placeholder="Describe the risk..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Before Controls */}
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900">
                <h5 className="font-medium text-red-800 dark:text-red-300 mb-3">Before Controls (Inherent Risk)</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Impact</Label>
                    <Select 
                      value={String(risk.impact_before)}
                      onValueChange={(v) => updateRisk(risk.id, 'impact_before', parseInt(v))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue>{getImpactLabel(risk.impact_before)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {IMPACT_SCALE.map(item => (
                          <SelectItem key={item.value} value={String(item.value)}>
                            <div className="flex flex-col">
                              <span>{item.value} - {item.label}</span>
                              <span className="text-xs text-muted-foreground">{item.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Likelihood</Label>
                    <Select 
                      value={String(risk.likelihood_before)}
                      onValueChange={(v) => updateRisk(risk.id, 'likelihood_before', parseInt(v))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue>{getLikelihoodLabel(risk.likelihood_before)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {LIKELIHOOD_SCALE.map(item => (
                          <SelectItem key={item.value} value={String(item.value)}>
                            <div className="flex flex-col">
                              <span>{item.value} - {item.label}</span>
                              <span className="text-xs text-muted-foreground">{item.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Score:</span>
                    <Badge className={getRiskBadgeColor(risk.level_before)}>
                      {risk.score_before} - {RISK_LEVELS[risk.level_before]?.label}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{RISK_LEVELS[risk.level_before]?.action}</p>
              </div>

              {/* After Controls */}
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-green-800 dark:text-green-300">After Controls (Residual Risk)</h5>
                  {estimatingRisks[risk.id] && (
                    <Badge variant="outline" className="text-xs flex items-center gap-1 animate-pulse">
                      <Sparkles className="h-3 w-3" />
                      Estimating...
                    </Badge>
                  )}
                  {risk.control_coverage !== undefined && risk.control_coverage > 0 && !estimatingRisks[risk.id] && (
                    <Badge variant="outline" className="text-xs flex items-center gap-1 bg-green-100 dark:bg-green-900/30">
                      <Sparkles className="h-3 w-3 text-green-600" />
                      {Math.round(risk.control_coverage)}% Coverage
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Impact</Label>
                    <Select 
                      value={String(risk.impact_after)}
                      onValueChange={(v) => updateRisk(risk.id, 'impact_after', parseInt(v))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue>{getImpactLabel(risk.impact_after)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {IMPACT_SCALE.map(item => (
                          <SelectItem key={item.value} value={String(item.value)}>
                            <div className="flex flex-col">
                              <span>{item.value} - {item.label}</span>
                              <span className="text-xs text-muted-foreground">{item.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Likelihood</Label>
                    <Select 
                      value={String(risk.likelihood_after)}
                      onValueChange={(v) => updateRisk(risk.id, 'likelihood_after', parseInt(v))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue>{getLikelihoodLabel(risk.likelihood_after)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {LIKELIHOOD_SCALE.map(item => (
                          <SelectItem key={item.value} value={String(item.value)}>
                            <div className="flex flex-col">
                              <span>{item.value} - {item.label}</span>
                              <span className="text-xs text-muted-foreground">{item.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Score:</span>
                    <Badge className={getRiskBadgeColor(risk.level_after)}>
                      {risk.score_after} - {RISK_LEVELS[risk.level_after]?.label}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{RISK_LEVELS[risk.level_after]?.action}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Existing Controls</Label>
                  {openControlLibrary && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openControlLibrary(risk.id)}
                      className="h-7 text-xs"
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      Select from Library
                    </Button>
                  )}
                </div>
                
                {/* Selected Controls from Library */}
                {risk.selected_controls && risk.selected_controls.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-muted/50 rounded-md">
                    {risk.selected_controls.map((control, idx) => (
                      <Badge 
                        key={`${control.control_id}-${idx}`}
                        variant="secondary"
                        className="pl-2 pr-1 py-1 flex items-center gap-1 text-xs"
                      >
                        <Shield className="h-3 w-3 text-green-600" />
                        {control.control_name}
                        {removeSelectedControl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSelectedControl(risk.id, control);
                            }}
                            className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Custom controls text area */}
                <div className="relative">
                  {risk.library_risk_id && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                      <Sparkles className="h-3 w-3" />
                      AI will auto-estimate risk reduction
                    </span>
                  )}
                  <Textarea 
                    value={risk.existing_controls}
                    onChange={(e) => updateRisk(risk.id, 'existing_controls', e.target.value)}
                    placeholder={risk.selected_controls?.length > 0 
                      ? "Add custom controls or notes..." 
                      : risk.library_risk_id 
                        ? "Type controls to see AI-estimated risk reduction..." 
                        : "Select from library or type custom controls..."}
                    rows={2}
                    className={estimatingRisks[risk.id] ? "border-green-500/50 ring-1 ring-green-500/20" : ""}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Additional Controls Required</Label>
                  {openControlLibrary && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openControlLibrary(risk.id)}
                      className="h-7 text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Browse Controls
                    </Button>
                  )}
                </div>
                <Textarea 
                  value={risk.additional_controls}
                  onChange={(e) => updateRisk(risk.id, 'additional_controls', e.target.value)}
                  placeholder="New controls to be implemented..."
                  rows={3}
                />
              </div>
            </div>

            <Separator />

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={risk.measures_approved}
                  onCheckedChange={(checked) => updateRisk(risk.id, 'measures_approved', checked)}
                />
                <Label className="text-sm">Measures Approved</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Control Owner:</Label>
                <Input 
                  className="h-8 w-40"
                  value={risk.control_owner}
                  onChange={(e) => updateRisk(risk.id, 'control_owner', e.target.value)}
                  placeholder="Name"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Implementation Date:</Label>
                <Input 
                  type="date"
                  className="h-8 w-40"
                  value={risk.implementation_date}
                  onChange={(e) => updateRisk(risk.id, 'implementation_date', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {formData.risks.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-muted-foreground">
            <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No risks added yet. Click "Add Custom Risk" to begin your risk assessment.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
